import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  twoFactorStatus,
  twoFactorSetup,
  twoFactorEnable,
  twoFactorDisable,
} from "../api/auth";
import {
  listDispositions,
  listCodecs,
  createDisposition,
  updateDisposition,
  deleteDisposition,
} from "../api/lookup";

function DispositionsPanel() {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["dispositions"],
    queryFn: listDispositions,
  });
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [editId, setEditId] = useState<number | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["dispositions"] });
  const reset = () => {
    setName("");
    setDesc("");
    setEditId(null);
  };

  const save = useMutation({
    mutationFn: () =>
      editId
        ? updateDisposition(editId, { name, description: desc })
        : createDisposition({ name, description: desc }).then(() => undefined),
    onSuccess: () => {
      reset();
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteDisposition(id),
    onSuccess: invalidate,
  });

  if (isLoading) return <p className="text-gray-500">Loading dispositions…</p>;
  if (isError) return <p className="text-red-600">{(error as Error).message}</p>;
  const rows = data ?? [];
  return (
    <div className="space-y-3">
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Disposition</th>
              <th className="px-4 py-2">Description</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 ? (
              <tr><td colSpan={3} className="px-4 py-3 text-gray-500">None.</td></tr>
            ) : (
              rows.map((d) => (
                <tr key={d.disposition_id}>
                  <td className="px-4 py-2 font-medium">{d.name}</td>
                  <td className="px-4 py-2 text-gray-600">{d.description || "—"}</td>
                  <td className="px-4 py-2 text-right space-x-3">
                    <button
                      onClick={() => {
                        setEditId(d.disposition_id);
                        setName(d.name);
                        setDesc(d.description);
                      }}
                      className="text-brand hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete disposition "${d.name}"?`)) remove.mutate(d.disposition_id);
                      }}
                      className="text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">{editId ? "Edit" : "New"} disposition</label>
          <input
            className="border border-gray-300 rounded px-3 py-2 text-sm"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Description</label>
          <input
            className="border border-gray-300 rounded px-3 py-2 text-sm w-64"
            placeholder="Optional"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
          />
        </div>
        <button
          disabled={!name || save.isPending}
          onClick={() => save.mutate()}
          className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm disabled:opacity-50"
        >
          {editId ? "Save" : "Add"}
        </button>
        {editId && (
          <button onClick={reset} className="bg-gray-100 hover:bg-gray-200 rounded px-4 py-2 text-sm">
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

function CodecsPanel() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["codecs"],
    queryFn: listCodecs,
  });
  if (isLoading) return <p className="text-gray-500">Loading codecs…</p>;
  if (isError) return <p className="text-red-600">{(error as Error).message}</p>;
  const rows = data ?? [];
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-gray-500">
          <tr>
            <th className="px-4 py-2">Codec</th>
            <th className="px-4 py-2">Value</th>
            <th className="px-4 py-2">Enabled</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((c) => (
            <tr key={c.codec_id}>
              <td className="px-4 py-2 font-medium">{c.codec_name}</td>
              <td className="px-4 py-2 font-mono text-gray-600">{c.codec_value}</td>
              <td className="px-4 py-2">{c.active ? "✓" : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TwoFactorPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["2fa-status"], queryFn: twoFactorStatus });
  const [setup, setSetup] = useState<{ secret: string; otpauth_uri: string } | null>(null);
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState<string | null>(null);

  const begin = useMutation({
    mutationFn: twoFactorSetup,
    onSuccess: (d) => { setSetup(d); setMsg(null); },
  });
  const enable = useMutation({
    mutationFn: () => twoFactorEnable(code),
    onSuccess: () => { setSetup(null); setCode(""); setMsg("Two-factor authentication is now on."); qc.invalidateQueries({ queryKey: ["2fa-status"] }); },
    onError: () => setMsg("That code didn't match — try again."),
  });
  const disable = useMutation({
    mutationFn: () => twoFactorDisable(code),
    onSuccess: () => { setCode(""); setMsg("Two-factor authentication disabled."); qc.invalidateQueries({ queryKey: ["2fa-status"] }); },
    onError: () => setMsg("That code didn't match — try again."),
  });

  if (isLoading) return <p className="text-gray-500">Loading…</p>;
  const enabled = data?.enabled ?? false;

  return (
    <div className="bg-white rounded-lg shadow p-5 max-w-lg">
      <div className="flex items-center gap-3 mb-2">
        <span
          className={`px-2 py-0.5 rounded text-xs ${enabled ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"}`}
        >
          {enabled ? "Enabled" : "Disabled"}
        </span>
        <span className="text-sm text-gray-500">Authenticator app (TOTP)</span>
      </div>
      {msg && <p className="text-sm text-brand mb-3">{msg}</p>}

      {!enabled && !setup && (
        <button onClick={() => begin.mutate()} disabled={begin.isPending}
          className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm disabled:opacity-50">
          {begin.isPending ? "Preparing…" : "Enable two-factor authentication"}
        </button>
      )}

      {!enabled && setup && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Add this key to your authenticator app (Google Authenticator, Authy, 1Password…), then enter the
            6-digit code to confirm.
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded px-3 py-2 font-mono text-sm break-all">
            {setup.secret}
          </div>
          <details className="text-xs text-gray-500">
            <summary className="cursor-pointer">Show setup URI (for QR apps)</summary>
            <div className="font-mono break-all mt-1">{setup.otpauth_uri}</div>
          </details>
          <div className="flex gap-2 items-center">
            <input
              inputMode="numeric" maxLength={6} placeholder="123456"
              className="border border-gray-300 rounded px-3 py-2 text-sm w-32 text-center tracking-widest"
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
            <button onClick={() => enable.mutate()} disabled={code.length < 6 || enable.isPending}
              className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm disabled:opacity-50">
              Confirm &amp; enable
            </button>
            <button onClick={() => { setSetup(null); setCode(""); }} className="text-sm text-gray-500">Cancel</button>
          </div>
        </div>
      )}

      {enabled && (
        <div className="flex gap-2 items-center">
          <input
            inputMode="numeric" maxLength={6} placeholder="Code to disable"
            className="border border-gray-300 rounded px-3 py-2 text-sm w-40 text-center tracking-widest"
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          />
          <button onClick={() => disable.mutate()} disabled={code.length < 6 || disable.isPending}
            className="bg-red-600 hover:bg-red-700 text-white rounded px-4 py-2 text-sm disabled:opacity-50">
            Disable
          </button>
        </div>
      )}
    </div>
  );
}

export function SettingsPage() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold mb-4">Two-Factor Authentication</h1>
        <TwoFactorPanel />
      </div>
      <div>
        <h2 className="text-xl font-semibold mb-3">Call Dispositions</h2>
        <DispositionsPanel />
      </div>
      <div>
        <h2 className="text-xl font-semibold mb-3">Audio Codecs</h2>
        <CodecsPanel />
      </div>
    </div>
  );
}
